import { Tooltip, TooltipPopper } from '@zillow/constellation';
import { Box, Card, Heading, Paragraph, Text } from '@zillow/constellation-10';
import {
  CREDIT_SCORE_LACK_OF_INFORMATION,
  getCreditUsed,
  getEstimatedMonthlyPayments,
  getNumberFromString,
  getOnTimePaymentsPercent,
  getTotalDebt,
} from '@zg-rentals/applications-utils';
import type { CreditReportType } from '@zg-rentals/ts-capps-client';
import { UserType } from '../../models/report-models';

export function CreditReportSummary({
  creditReport,
  userType,
}: {
  creditReport: CreditReportType;
  userType?: UserType;
}) {
  const creditScore = getNumberFromString(creditReport.creditScore);
  const metricValueOnError = creditScore === CREDIT_SCORE_LACK_OF_INFORMATION ? 'N/A' : null;

  const onTimePaymentsPercent = metricValueOnError ?? getOnTimePaymentsPercent(creditReport);
  const estimatedMonthlyPayments = metricValueOnError ?? getEstimatedMonthlyPayments(creditReport);
  const creditUsed = metricValueOnError ?? getCreditUsed(creditReport);
  const totalDebt = metricValueOnError ?? getTotalDebt(creditReport);
  return (
    <>
      <Heading level={3} textStyle="heading-sm" css={{ marginTop: 'md', marginBottom: 'md' }}>
        Financial summary
      </Heading>

      <Box css={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 'sm', marginBottom: 'sm' }}>
        <Box css={{ gridColumnEnd: { base: 'span 4', md: 'span 2', xl: 'span 1' } }}>
          <Card elevated={false}>
            <Box css={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flexDirection: 'column' }}>
              <Paragraph>On-time payments</Paragraph>
              <Text textStyle="heading-md" css={{ marginTop: 'sm', marginBottom: 'sm' }}>
                {onTimePaymentsPercent ? onTimePaymentsPercent : 'None found'}
              </Text>
            </Box>
          </Card>
        </Box>
        <Box css={{ display: 'grid', gridColumnEnd: { base: 'span 4', md: 'span 2', xl: 'span 1' } }}>
          <Card elevated={false}>
            <Box css={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flexDirection: 'column' }}>
              <Paragraph>
                Est. mo. payments{' '}
                <TooltipPopper
                  triggered={
                    <Tooltip
                      body={
                        <Paragraph>
                          This is the total amount of minimum payments due from all revolving accounts (such as credit
                          cards) and installment accounts (such as active student loans, auto loans or mortgage
                          payments).
                        </Paragraph>
                      }
                    />
                  }
                />
              </Paragraph>
              <Text textStyle="heading-md" css={{ marginTop: 'sm', marginBottom: 'sm' }}>
                {estimatedMonthlyPayments ? estimatedMonthlyPayments : 'None found'}
              </Text>
            </Box>
          </Card>
        </Box>
        <Box css={{ display: 'grid', gridColumnEnd: { base: 'span 4', md: 'span 2', xl: 'span 1' } }}>
          <Card elevated={false}>
            <Box css={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flexDirection: 'column' }}>
              <Paragraph>
                Credit used{' '}
                <TooltipPopper
                  triggered={
                    <Tooltip
                      body={
                        <Paragraph>
                          This is the percentage of available credit being used. For example, if there is one credit
                          card with a $5,000 limit and the balance is $1,000, 20% of available credit is used.
                          {userType == UserType.Renter
                            ? ' A high percentage can negatively affect your credit score.'
                            : ''}
                        </Paragraph>
                      }
                    />
                  }
                />
              </Paragraph>
              <Text textStyle="heading-md" css={{ marginTop: 'sm', marginBottom: 'sm' }}>
                {creditUsed ? creditUsed : 'None found'}
              </Text>
            </Box>
          </Card>
        </Box>
        <Box css={{ display: 'grid', gridColumnEnd: { base: 'span 4', md: 'span 2', xl: 'span 1' } }}>
          <Card elevated={false}>
            <Box css={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flexDirection: 'column' }}>
              <Paragraph>Total debt</Paragraph>
              <Text textStyle="heading-md" css={{ marginTop: 'sm', marginBottom: 'sm' }}>
                {totalDebt ? totalDebt : 'None found'}
              </Text>
            </Box>
          </Card>
        </Box>
      </Box>
    </>
  );
}
